import { useEffect, useState } from "react";
import { Modal } from "../../components/Modal";
import { Button } from "../../components/ui/button";
import { Textarea } from "../../components/ui/textarea";

const COMMENT_MAX = 1000;

export interface LogInsightsDecisionModalProps {
  open: boolean;
  title: string;
  confirmLabel: string;
  confirmVariant: "success" | "danger";
  selectedCount: number;
  /** Disables the form + shows a submitting label while the request is in flight. */
  submitting?: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: (comment: string) => void;
}

/**
 * Confirmation step for an Acknowledge / Approve / Reject decision. A comment
 * is mandatory — it lands in the audit trail the Info popup renders — and the
 * modal refuses to dismiss while the request is in flight.
 */
export const LogInsightsDecisionModal = ({
  open,
  title,
  confirmLabel,
  confirmVariant,
  selectedCount,
  submitting = false,
  onOpenChange,
  onConfirm,
}: LogInsightsDecisionModalProps) => {
  const [comment, setComment] = useState("");

  useEffect(() => {
    if (open) setComment("");
  }, [open]);

  const disabled = !comment.trim() || submitting;

  return (
    <Modal
      open={open}
      onClose={() => {
        // Block dismiss while a request is in flight.
        if (submitting) return;
        onOpenChange(false);
      }}
      title={title}
      closable={!submitting}
      footer={
        <>
          <Button
            variant="secondary"
            disabled={submitting}
            onClick={() => onOpenChange(false)}
          >
            Cancel
          </Button>
          <Button
            variant={confirmVariant === "danger" ? "destructive" : "default"}
            disabled={disabled}
            onClick={() => onConfirm(comment.trim())}
          >
            {submitting ? "Submitting…" : confirmLabel}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <p className="text-sm text-muted-foreground">
          No. of CR(s) Selected:{" "}
          <span className="font-semibold text-foreground">{selectedCount}</span>
        </p>

        <div className="flex flex-col gap-1.5">
          <div className="flex items-center justify-between">
            <label htmlFor="li-comment" className="text-sm font-semibold text-foreground">
              Comments <span className="text-destructive">*</span>
            </label>
            <span className="text-xs tabular-nums text-muted-foreground">
              {comment.length}/{COMMENT_MAX}
            </span>
          </div>
          <Textarea
            id="li-comment"
            rows={7}
            placeholder="Enter your comments here"
            value={comment}
            disabled={submitting}
            onChange={(e) => setComment(e.target.value.slice(0, COMMENT_MAX))}
          />
        </div>
      </div>
    </Modal>
  );
};
